import { useEffect, useState } from 'react';
import { Modal } from 'us-react-components/dist/Modal/Modal';
import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { Button, ButtonType } from '../../../../components/Button';
import { ModalBase } from './types';

// -- Certifications Edit Modal ---------------------------------------------
export const CertificationsEditModal = ({
  isOpen,
  onClose,
  certifications,
  initialSelected,
  onSave,
}: ModalBase & {
  certifications: Array<{ id: number; name: string }>;
  initialSelected: Array<{ id: number; name: string }>;
  onSave: (selected: Array<{ id: number; name: string }>) => Promise<void>;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const [selected, setSelected] = useState<Record<string, { id: number; name: string }>>(
    () => Object.fromEntries(initialSelected.map((c) => [c.name, c])),
  );
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelected(Object.fromEntries(initialSelected.map((c) => [c.name, c])));
    }
  }, [isOpen, initialSelected]);

  const toggle = (cert: { id: number; name: string }): void => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[cert.name]) delete next[cert.name];
      else next[cert.name] = cert;
      return next;
    });
  };

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    try {
      await onSave(Object.values(selected));
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const col1 = certifications.slice(0, 5);
  const col2 = certifications.slice(5, 10);

  const renderCol = (list: Array<{ id: number; name: string }>): JSX.Element => (
    <div className='space-y-2'>
      {list.map((cert) => (
        <label key={cert.id} className='flex items-start gap-2 cursor-pointer'>
          <input
            id={`certification-${cert.name}`}
            type='checkbox'
            checked={!!selected[cert.name]}
            onChange={(): void => toggle(cert)}
            className='mt-1'
          />
          <span>{cert.name}</span>
        </label>
      ))}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('pages.sitterProfile.modals.editCertifications')}
      className='w-full max-w-xl'
      footer={
        <>
          <Button buttonType={ButtonType.tertiary} onClick={onClose}>
            Cancel
          </Button>
          <Button
            id='save-certifications'
            buttonType={ButtonType.primary}
            disabled={submitting}
            onClick={submit}
          >
            Save
          </Button>
        </>
      }
    >
      <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3 text-base'>
        {renderCol(col1)}
        {renderCol(col2)}
      </div>
    </Modal>
  );
};
